import { useCiamStore } from '@/features/auth/azure-ad'
import { useImpersonnalisationProvider } from '@/features/auth/impersonnalisation'
import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'
import { useContextsAppProvider } from '../../context-app/useContextsAppProvider'
import { AuthAktoEtats, type ICiam, type IContextsAppProvider, Utilisateur, UtilisateurBase } from '../modeles'

export const useAuthentificationStore = defineStore('authentification', () => {
  // Azure AD
  const ciam: ICiam = useCiamStore()

  // Initialisation
  let promesse: Promise<boolean> | null = null

  // utilisateur
  const utilisateur = shallowRef<UtilisateurBase | null>(null)
  const etat = ref<AuthAktoEtats>(AuthAktoEtats.Initialisation)

  async function initializeAsync(to: string): Promise<boolean> {
    await attendreFlotAuthentificationAsync()
    return await chargementUtilisateurAsync(to)
  }

  async function authentificationRequiseAsync(to: string, inscription: boolean): Promise<boolean> {
    // Etape 1 - être authentifié par le SSO
    const estAuthentifiéCIAM = await ciam.authentificationRequiseAsync(to, inscription)
    if (estAuthentifiéCIAM === false) return false
    return await chargementUtilisateurAsync(to)
  }

  function attendreFlotAuthentificationAsync(): Promise<void> {
    return ciam.attendreFlotAuthentificationAsync()
  }

  async function chargementUtilisateurAsync(to: string): Promise<boolean> {
    const idUtilisateur = ciam.getIdUtilisateurCourant()
    if (!idUtilisateur) {
      if (etat.value !== AuthAktoEtats.NonAuthentifié) {
        setNonAuthentifié()
      }
      return false
    }

    if (etat.value === AuthAktoEtats.Authentifié && idUtilisateur === utilisateur.value?.userId) {
      return true
    }

    let resolveAuthentification: ((value: boolean | PromiseLike<boolean>) => void) | null = null
    if (promesse === null) {
      promesse = new Promise<boolean>((resolve) => (resolveAuthentification = resolve))
    } else {
      return await promesse
    }

    try {
      // Etape 2 - voir si l'utilisateur est cohérent avec celui stocké
      if (idUtilisateur !== utilisateur.value?.userId) {
        // Charge l'utilisateur
        utilisateur.value = await ciam.getUtilisateurCourantAsync()
        etat.value = AuthAktoEtats.AuthentifiéCiam
      }

      // Etape 3 - si c'est un interne impersonnaliser si nécessaire
      if (utilisateur.value?.estInterne === true) {
        if (etat.value === AuthAktoEtats.AuthentifiéCiam) {
          etat.value = AuthAktoEtats.EnImpersonnalisation
          // Impersonalisation
          const impersonalisationProvider = useImpersonnalisationProvider()
          const data = await impersonalisationProvider.getImpersonnalisationAsync(to)
          utilisateur.value = new Utilisateur(utilisateur.value, data.token, data.email, data.nom, data.prenom)
          etat.value = AuthAktoEtats.Impersonnalisé
        }
      }

      // Etape 4 - charger la typologie du portefeuille et les permissions
      if ([AuthAktoEtats.AuthentifiéCiam, AuthAktoEtats.Impersonnalisé].includes(etat.value)) {
        etat.value = AuthAktoEtats.EnChargementPortefeuille
        const contextProvider: IContextsAppProvider = useContextsAppProvider()
        const contextsApp = await contextProvider.getContextsAppAsync()

        utilisateur.value = utilisateur.value!.clone({
          contextsApp,
        })
        etat.value = AuthAktoEtats.Authentifié
        // Todo : Fixer un context par défault
      }

      const estAuthentifié = etat.value === AuthAktoEtats.Authentifié
      resolveAuthentification!(estAuthentifié)

      return estAuthentifié
    } catch {
      await deconnexionAsync()
      resolveAuthentification!(false)

      return false
    } finally {
      promesse = null
    }
  }

  async function obtientHeadersAsync(): Promise<HeadersInit> {
    if (!utilisateur.value) return {}

    const headers: Record<string, string> = {}

    const bearerToken = await ciam.getBearerTokenAsync()
    headers['Authorization'] = `Bearer ${bearerToken}`
    if (utilisateur.value instanceof Utilisateur && utilisateur.value.estImpersonnalisé) {
      headers['x-akto-impersonnalisation'] = utilisateur.value.impresonnaliseToken
    }

    return headers
  }

  async function deconnexionAsync() {
    try {
      // Deconnexion du ciam
      await ciam.deconnexionAsync()
    } finally {
      // Nettoyage de l'utilisateur
      setNonAuthentifié()
    }
  }

  async function deconnexionImpersonnalisationAsync() {
    utilisateur.value = new UtilisateurBase(
      utilisateur.value!.userId,
      utilisateur.value!.userId,
      utilisateur.value!.nom,
      utilisateur.value!.prenom,
      null,
      utilisateur.value!.estInterne,
    )
    etat.value = AuthAktoEtats.AuthentifiéCiam
    promesse = null
    return await chargementUtilisateurAsync("/")
  }

  function setNonAuthentifié() {
    utilisateur.value = null
    etat.value = AuthAktoEtats.NonAuthentifié
    promesse = null
  }

  return {
    utilisateur,
    etat,
    initializeAsync,
    attendreFlotAuthentificationAsync,
    authentificationRequiseAsync,
    deconnexionAsync,
    deconnexionImpersonnalisationAsync,
    obtientHeadersAsync,
  }
})
